/**
 * Payment-in-flight lock.
 *
 * Creating a payment session happens moments after a brand-new member is
 * signed in. Signing in fires an auth event, and the app responds by
 * refreshing the current page's data (router invalidation) — which tears
 * down the in-flight payment request before the payment page can be
 * returned. The member then lands on "something went wrong" instead of the
 * card page.
 *
 * While a payment request is running we hold this lock. The auth listener
 * checks it and defers the page refresh until the lock clears, so the
 * payment request is never cut off. This removes the race itself; the
 * retry in the signup flow stays as a second safety net for genuinely
 * flaky connections.
 */

let inFlight = 0;
const waiting: Array<() => void> = [];

export function beginCheckout(): void {
  inFlight += 1;
}

export function endCheckout(): void {
  inFlight = Math.max(0, inFlight - 1);
  if (inFlight === 0) {
    while (waiting.length) waiting.shift()?.();
  }
}

export function isCheckoutInFlight(): boolean {
  return inFlight > 0;
}

/** Run `fn` now, or as soon as no payment request is in flight. */
export function afterCheckout(fn: () => void): void {
  if (inFlight === 0) {
    fn();
    return;
  }
  waiting.push(fn);
  // Never hold a queued refresh forever if something goes wrong.
  setTimeout(() => {
    const i = waiting.indexOf(fn);
    if (i >= 0) {
      waiting.splice(i, 1);
      fn();
    }
  }, 15_000);
}
